import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Database,
  FileUp,
  FolderKanban,
  Inbox,
  Mail,
  Plus,
  RefreshCw,
  Target,
  UserPlus,
  Users,
} from "lucide-react";
import DashboardHeader from "../../components/dashboard/DashboardHeader";
import KpiCard from "../../components/dashboard/KpiCard";
import ProjectMixCard from "../../components/dashboard/ProjectMixCard";
import ProjectsAttentionTable from "../../components/dashboard/ProjectsAttentionTable";
import PerformanceSnapshotCard from "../../components/dashboard/PerformanceSnapshotCard";
import WebsiteLeadsCard from "../../components/dashboard/WebsiteLeadsCard";
import NoticeBoardCard from "../../components/notices/NoticeBoardCard";
import {
  fetchAdminKpis,
  fetchRecordKpis,
  RECENT_DAYS,
} from "../../controllers/dashboardController";

// `openCreate` state tells the target page to open its create modal on arrival.
const QUICK_ACTIONS = [
  { label: "New Project", icon: Plus, to: "/admin/project-management", state: { openCreate: true }, primary: true },
  { label: "Import Data", icon: FileUp, to: "/admin/import-data" },
  { label: "Add User", icon: UserPlus, to: "/admin/user-management", state: { openCreate: true } },
  { label: "SalesQL Sync", icon: RefreshCw, to: "/admin/salesql-sync" },
];

const fmtN = (n) => Number(n || 0).toLocaleString();
const UNAVAILABLE = { value: "—", sub: "Couldn't load" };

const leadValue = ({ recent, recentIsLowerBound }) =>
  `${fmtN(recent)}${recentIsLowerBound ? "+" : ""}`;

// Each builder turns one `fetchAdminKpis` section into KpiCard props.
const buildCards = (kpis) => {
  const { projects, records, experts, contacts, hr } = kpis;

  return [
    {
      key: "projects",
      label: "Active Projects",
      icon: FolderKanban,
      color: "blue",
      to: "/admin/project-management",
      ...(projects.data
        ? {
            value: fmtN(projects.data.active),
            sub: `${fmtN(projects.data.overdue)} overdue`,
            badge: projects.data.overdue > 0 ? "Attention" : undefined,
          }
        : UNAVAILABLE),
    },
    {
      key: "recruitment",
      label: "Recruitment Progress",
      icon: Target,
      color: "green",
      to: "/admin/project-management",
      ...(projects.data
        ? {
            value: projects.data.targets
              ? `${Math.round((projects.data.recruited / projects.data.targets) * 100)}%`
              : "—",
            sub: `${fmtN(projects.data.recruited)} of ${fmtN(projects.data.targets)} recruited`,
          }
        : UNAVAILABLE),
    },
    {
      key: "records",
      label: "Records",
      icon: Database,
      color: "dark",
      to: "/admin/database-mangement",
      ...(records.data
        ? {
            value: fmtN(records.data.total),
            sub: `Warm ${fmtN(records.data.warm)} · Cold ${fmtN(records.data.cold)}`,
          }
        : UNAVAILABLE),
    },
    {
      key: "experts",
      label: `Expert Requests (${RECENT_DAYS}d)`,
      icon: Inbox,
      color: "purple",
      to: "/admin/expert-requests",
      ...(experts.data
        ? { value: leadValue(experts.data), sub: `${fmtN(experts.data.total)} all time` }
        : UNAVAILABLE),
    },
    {
      key: "contacts",
      label: `Contact Queries (${RECENT_DAYS}d)`,
      icon: Mail,
      color: "orange",
      to: "/admin/contact-queries",
      ...(contacts.data
        ? { value: leadValue(contacts.data), sub: `${fmtN(contacts.data.total)} all time` }
        : UNAVAILABLE),
    },
    {
      key: "employees",
      label: "Employees",
      icon: Users,
      color: "blue",
      to: "/hr/employees",
      ...(hr.data
        ? {
            value: fmtN(hr.data.total),
            sub: `${fmtN(hr.data.active)} active`,
            badge:
              hr.data.docsExpiring > 0
                ? `${fmtN(hr.data.docsExpiring)} docs expiring`
                : undefined,
          }
        : UNAVAILABLE),
    },
  ];
};

const PLACEHOLDER_KEYS = ["projects", "records", "experts", "contacts", "hr"];
const LOADING_KPIS = Object.fromEntries(PLACEHOLDER_KEYS.map((k) => [k, {}]));

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchAdminKpis().then((result) => {
      if (!cancelled) setKpis(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // After a convert-to-warm: mark the lead converted, then refresh the record
  // totals so the Records card picks up the new warm record.
  const handleLeadConverted = (id) => {
    setKpis((prev) => {
      const experts = prev?.experts?.data;
      if (!experts) return prev;
      const latest = experts.latest.map((r) =>
        r.id === id ? { ...r, convertedToWarm: true } : r,
      );
      return { ...prev, experts: { data: { ...experts, latest } } };
    });
    fetchRecordKpis()
      .then((data) => setKpis((prev) => ({ ...prev, records: { data } })))
      .catch(() => {});
  };

  const isLoading = kpis === null;
  const cards = buildCards(kpis || LOADING_KPIS);
  const projects = kpis?.projects?.data;
  const projectsFailed = Boolean(kpis?.projects?.error);

  return (
    <div className="w-full max-w-full space-y-6">
      <DashboardHeader
        subtitle="Here's what's happening across projects, records and your team."
        actions={QUICK_ACTIONS}
      />

      <section
        aria-label="Key metrics"
        className="grid grid-cols-2 gap-4 md:grid-cols-3 2xl:grid-cols-6"
      >
        {cards.map(({ key, to, ...card }) => (
          <KpiCard
            key={key}
            {...card}
            loading={isLoading}
            onClick={() => navigate(to)}
          />
        ))}
      </section>

      <NoticeBoardCard />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-8">
          <ProjectsAttentionTable
            projects={projects?.attention}
            loading={isLoading}
            error={projectsFailed}
          />
        </div>
        <div className="min-w-0 xl:col-span-4">
          <ProjectMixCard
            byPriority={projects?.byPriority}
            byType={projects?.byType}
            total={projects?.active}
            loading={isLoading}
            error={projectsFailed}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-7">
          <PerformanceSnapshotCard
            data={kpis?.performance?.data}
            loading={isLoading}
            error={Boolean(kpis?.performance?.error)}
          />
        </div>
        <div className="min-w-0 xl:col-span-5">
          <WebsiteLeadsCard
            experts={kpis?.experts}
            contacts={kpis?.contacts}
            loading={isLoading}
            onConverted={handleLeadConverted}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
